import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, computed, observable } from 'mobx'
import type { Id } from '@/infra/id'
import { type BookkeepingProgressionStore } from '@/features/jobs/bookkeeping/BookkeepingProgressionStore'
import {
  BookkeepingCycle,
  getBookkeepingCycleCompletedAt,
  getBookkeepingCycleWorkItemsCompletion,
} from '@/features/jobs/bookkeeping/BookkeepingProgressionInfo'
import { formatBookkeepingCycleStatus } from '@/features/jobs/bookkeeping/bookkeeping-cycle-formatters'
import { isNotNil } from '@/utils/object-util'

/**
 * A work item and how many of it the cycle completed.
 */
export interface CompletedWorkItem {
  name: string
  quantity: number
}

/**
 * Shows what a bookkeeping cycle completed.
 */
export class BookkeepingCycleDetailsDialogViewModel {
  /**
   * The dialog's modal state.
   */
  readonly modal = new ModalViewModel()

  /**
   * The cycle being shown.
   */
  @observable accessor cycle: BookkeepingCycle = BookkeepingCycle.empty

  constructor(
    private readonly _jobId: Id,
    private readonly _bookkeepingProgressionStore: BookkeepingProgressionStore,
  ) {}

  /**
   * Returns the bookkeeping progression info.
   */
  @computed
  get bookkeepingProgressionInfo() {
    return this._bookkeepingProgressionStore.bookkeepingProgressions.get(
      this._jobId,
    )
  }

  /**
   * The status of the cycle, formatted for display.
   */
  @computed
  get statusName() {
    return formatBookkeepingCycleStatus(this.cycle.status)
  }

  /**
   * The date the cycle was completed, if it was.
   */
  @computed
  get completedAt() {
    return getBookkeepingCycleCompletedAt(this.cycle) ?? undefined
  }

  /**
   * Whether the cycle is completed.
   */
  @computed
  get isCompleted() {
    return this.cycle.status.type === 'Completed'
  }

  /**
   * Returns a list of bookkeeping completed work items in a cycle.
   */
  @computed
  get completedWorkItemsInCycle(): CompletedWorkItem[] {
    return (this.bookkeepingProgressionInfo?.workItems ?? [])
      .map((workItem) => {
        const completeItem = getBookkeepingCycleWorkItemsCompletion(
          this.cycle,
        ).find((ci) => ci.id === workItem.workItemId)

        if (!completeItem) {
          return null
        }

        return {
          name: completeItem.name,
          quantity: completeItem.completedCount,
        }
      })
      .filter(isNotNil)
  }

  /**
   * Sets the cycle to be selected and shows the dialog.
   */
  @action.bound
  open(selectedCycle: BookkeepingCycle) {
    this.cycle = selectedCycle
    this.modal.show()
  }
}
